import { FC, useMemo } from "react";
import styles from "./ProgressDetail.module.less";
import { HeatIcon } from "../heatIcon/HeatIcon";
import { Typography } from "antd";

interface ProgressDetailProps {
  max: number;
  current: number;
  predicted: number;
  risk?: number[];
  repair: number[];
  ettoList: { heatNumber: number; etto: number }[];
  style?: React.CSSProperties;
}

const ProgressDetail: FC<ProgressDetailProps> = ({
  max,
  current,
  predicted,
  risk,
  repair,
  ettoList,
  style,
}) => {
  const progressRender = useMemo(() => {
    return Array.from({ length: max }, (_, index) => index).map((i) => {
      const adjustedIndex = i + 1;

      const lastCurrent = current >= max && adjustedIndex === max;
      const riskCurrent =
        risk?.some((x) => x === current) && current === adjustedIndex;
      const repairCurrent = repair.some(
        (x) => x === current && current === adjustedIndex,
      );
      const repairHeat = repair?.some((x) => x === adjustedIndex);

      const slaglineHeat =
        repair.length > 0 ? current - repair[repair.length - 1] : current;

      const etto = ettoList.find((x) => x.heatNumber === adjustedIndex)?.etto;
      if (adjustedIndex == current || lastCurrent) {
        return (
          <div
            className={`${styles.bar} ${styles.current} ${styles.opened}`}
            key={adjustedIndex}
          >
            <img src="/icons/current_icon.svg" alt="" />
            <div className={styles.description}>
              <Typography.Title level={3}>{adjustedIndex}</Typography.Title>

              <div className={styles.slagLineRepairContainer}>
                <Typography.Title level={3}>SL</Typography.Title>
                <HeatIcon color="red" width="18" height="18" />
                <Typography.Title level={3}>{slaglineHeat}</Typography.Title>
              </div>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Current Heat
            </Typography.Text>
          </div>
        );
      }
      if (repairHeat) {
        return (
          <div
            className={`${styles.bar} ${styles.repair} ${
              repairCurrent ? styles.opened : ""
            }`}
            key={adjustedIndex}
          >
            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Slagline Repair
            </Typography.Text>
          </div>
        );
      }
      if (etto !== undefined) {
        return (
          <div
            className={`${styles.bar} ${styles.etto}`}
            key={adjustedIndex + etto}
          >
            <div className={styles.description}>
              <span>{adjustedIndex}</span>
            </div>

            <Typography.Text className={styles.colorDescription}>
              ETTO = {etto.toFixed(0)} mins
            </Typography.Text>
          </div>
        );
      }

      if (risk?.some((x) => x === adjustedIndex)) {
        return (
          <div
            className={`${styles.bar} ${styles.risk} ${
              lastCurrent || riskCurrent || repairCurrent ? styles.opened : ""
            }`}
            key={adjustedIndex}
          >
            {riskCurrent && <img src="/icons/current_icon.svg" alt="" />}

            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>
            <Typography.Text className={styles.colorDescription}>
              Past Critical Heat
            </Typography.Text>
          </div>
        );
      }

      if (adjustedIndex < current) {
        return (
          <div
            className={`${styles.bar} ${styles.default} ${
              lastCurrent ? styles.opened : ""
            }`}
            key={adjustedIndex}
          >
            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Past Heat
            </Typography.Text>
          </div>
        );
      }

      if (adjustedIndex > current && adjustedIndex < predicted) {
        return (
          <div className={`${styles.bar} ${styles.next}`} key={adjustedIndex}>
            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Future Heat
            </Typography.Text>
          </div>
        );
      }

      if (adjustedIndex >= predicted) {
        return (
          <div
            className={`${styles.bar} ${styles.predicted} ${
              predicted !== adjustedIndex && max !== adjustedIndex
                ? ""
                : styles.opened
            }`}
            key={adjustedIndex}
          >
            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Estimated Campaign End
            </Typography.Text>
          </div>
        );
      }

      if (repair?.some((x) => x === adjustedIndex)) {
        return (
          <div
            className={`${styles.bar} ${styles.repair} ${
              lastCurrent || riskCurrent ? styles.opened : ""
            }`}
            key={adjustedIndex}
          >
            {riskCurrent && <img src="/icons/current_icon.svg" alt="" />}

            <div className={styles.description}>
              <Typography.Text>{adjustedIndex}</Typography.Text>
            </div>

            <Typography.Text className={styles.colorDescription}>
              Repair
            </Typography.Text>
          </div>
        );
      }
    });
  }, [max, current, predicted, risk, ettoList, repair]);

  return (
    <div className={styles.progressDetail} style={{ ...style }}>
      {progressRender}
    </div>
  );
};

export default ProgressDetail;
